
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';
import { Link } from 'react-router-dom';
import { useState } from 'react';

const areas = [
  {
    name: 'Miami–Fort Lauderdale',
    description: 'Miami-Dade, Broward & Palm Beach Counties',
    homes: '12,000+',
    image:
      'https://public.readdy.ai/ai/img_res/92ae28b9415f6797173ad7fa9aead8c3.jpg',
    highlights: ['Miami', 'Fort Lauderdale', 'Boca Raton', 'West Palm Beach'],
    accent: 'from-brand-red/80 to-brand-red/40',
  },
  {
    name: 'Tampa Bay Area',
    description: 'Hillsborough, Pinellas & Pasco Counties',
    homes: '8,200+',
    image:
      'https://readdy.ai/api/search-image?query=Beautiful%20aerial%20view%20of%20Tampa%20Bay%20Florida%20waterfront%20skyline%20during%20golden%20hour%20sunset%2C%20modern%20downtown%20buildings%20reflecting%20warm%20light%20on%20calm%20bay%20water%2C%20Bayshore%20Boulevard%20with%20palm%20trees%2C%20Clearwater%20Beach%20visible%20in%20distance%2C%20boats%20on%20sparkling%20water%2C%20lush%20green%20parks%20vibrant%20coastal%20city%20atmosphere%20professional%20drone%20photography%20ultra%20high%20resolution&width=600&height=420&seq=area-tampa-bay-home-02&orientation=landscape',
    highlights: ['Tampa', 'St. Petersburg', 'Clearwater', 'Wesley Chapel'],
    accent: 'from-brand-navy/80 to-brand-navy/40',
  },
  {
    name: 'Orlando / Central Florida',
    description: 'Orange, Seminole & Osceola Counties',
    homes: '9,500+',
    image:
      'https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20Orlando%20Central%20Florida%20cityscape%20at%20golden%20hour%2C%20modern%20downtown%20skyline%20with%20glass%20towers%2C%20beautiful%20Lake%20Eola%20fountain%20in%20foreground%2C%20lush%20green%20tropical%20trees%20and%20parks%20surrounding%20the%20lake%2C%20warm%20sunset%20glow%20on%20buildings%2C%20residential%20neighborhoods%20stretching%20to%20horizon%20professional%20drone%20photography%20ultra%20high%20resolution&width=600&height=420&seq=area-orlando-central-home-03&orientation=landscape',
    highlights: ['Orlando', 'Kissimmee', 'Winter Park', 'Sanford'],
    accent: 'from-brand-gold/80 to-brand-gold/40',
  },
];

export default function ServiceAreas() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, getItemStyle } = useStaggerAnimation(areas.length);

  // Simple fallback for broken images
  const handleImgError = (e) => {
    e.target.src = '/fallback-service-area.jpg';
  };

  return (
    <section className="py-16 lg:py-24 bg-brand-cream">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-10 lg:mb-16"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
            transition:
              'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1), transform 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <div className="inline-block bg-brand-red-light text-brand-red px-4 py-2 rounded-full text-sm font-semibold mb-4">
            WHERE WE SERVE
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Proudly Serving Florida&rsquo;s
            <br className="hidden sm:block" /> Major Metro Areas
          </h2>
          <p className="text-base lg:text-lg text-gray-600 max-w-2xl mx-auto">
            Our certified technicians are stationed across Florida&rsquo;s biggest regions, delivering expert HVAC, solar, and insulation services to your neighborhood.
          </p>
        </div>

        {/* Area Cards */}
        <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
          {areas.map((area, index) => (
            <article
              key={area.name}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 cursor-pointer"
              style={getItemStyle(index)}
            >
              {/* Image */}
              <div className="relative w-full h-[200px] sm:h-[240px] overflow-hidden">
                <img
                  src={area.image}
                  alt={`${area.name} energy services`}
                  title={`${area.name} HVAC solar insulation services`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  onError={handleImgError}
                />
                {/* Gradient overlay */}
                <div
                  className={`absolute inset-0 bg-gradient-to-t ${area.accent} to-transparent opacity-60`}
                ></div>
                <div className="absolute bottom-4 left-5 right-5">
                  <h4 className="text-xl sm:text-2xl font-bold text-white drop-shadow-lg">
                    <Link to="/service-areas">{area.name}</Link>
                  </h4>
                  <p className="text-white/90 text-sm drop-shadow">{area.description}</p>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6">
                {/* Stats row */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <i className="ri-home-4-line text-brand-red w-4 h-4 flex items-center justify-center"></i>
                    <span>
                      <strong className="text-brand-navy">{area.homes}</strong> homes served
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <i className="ri-shield-check-line text-brand-red w-4 h-4 flex items-center justify-center"></i>
                    <span>Licensed &amp; Insured</span>
                  </div>
                </div>

                {/* City pills */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {area.highlights.map((city) => (
                    <span
                      key={city}
                      className="inline-flex items-center gap-1 bg-brand-cream text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full"
                    >
                      <i className="ri-map-pin-2-fill text-brand-gold text-[10px] w-3 h-3 flex items-center justify-center"></i>
                      {city}
                    </span>
                  ))}
                </div>

                {/* CTA */}
                <Link
                  to="/service-areas"
                  className="inline-flex items-center gap-2 text-brand-red font-semibold text-sm hover:text-brand-red-dark transition-colors group/link"
                >
                  View Full Coverage
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center group-hover/link:translate-x-1 transition-transform"></i>
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className="mt-10 lg:mt-14 text-center"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(20px)',
            transition:
              'opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.5s, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) 0.5s',
          }}
        >
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 bg-brand-navy text-white px-8 py-4 rounded-full font-semibold hover:bg-brand-navy-dark transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <i className="ri-map-2-line w-5 h-5 flex items-center justify-center"></i>
            See All Service Areas
          </Link>
        </div>
      </div>
    </section>
  );
}
